import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useWorkspace } from '../app/WorkspaceProvider';
import { VideoGrid, VideoGridSkeleton } from '../components/VideoCard';
import { AppLink } from '../components/Nav';
import { RecordIcon, UsersIcon } from '../components/icons';
import {
  Badge,
  Banner,
  Button,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  cx,
} from '../components/ui';
import { errorMessage } from '../lib/api';
import { countLabel } from '../lib/format';
import type { OrgSummary } from '../lib/types';
import { qk } from '../queries/keys';
import { atLeast, roleLabel, subscriptionNotice } from '../queries/orgs';
import { fetchLibrary, libraryScopeKey, type LibraryQuery } from '../queries/videos';

/**
 * A team's workspace: what the team has recorded together, plus the one banner
 * that matters (trial ending, payment failed) and, for admins, a way to see
 * everything in the org rather than only the shared workspace.
 */

type Scope = 'team' | 'org';

function ScopeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'rounded px-3 py-1.5 text-body transition-colors duration-120',
        active
          ? 'bg-slate-800 text-chalk'
          : 'text-chalk-dim hover:bg-slate-850 hover:text-chalk',
      )}
    >
      {children}
    </button>
  );
}

function OrgWorkspace({ org }: { org: OrgSummary }) {
  const [scope, setScope] = useState<Scope>('team');
  const isAdmin = atLeast(org.role, 'admin');
  const effectiveScope: Scope = isAdmin ? scope : 'team';

  const query: LibraryQuery =
    effectiveScope === 'org'
      ? { scope: 'org', orgId: org.id }
      : { workspace: 'team', orgId: org.id };

  const videos = useQuery({
    queryKey: qk.videos(libraryScopeKey(query)),
    queryFn: () => fetchLibrary(query),
    staleTime: 15_000,
  });

  const notice = subscriptionNotice(org.subscriptionStatus, org.trialEndsAt);
  const isOwner = atLeast(org.role, 'owner');
  const list = videos.data?.videos ?? [];

  return (
    <>
      <PageHeader
        title={org.name}
        subtitle={
          <span>
            Your role: {roleLabel(org.role)} ·{' '}
            <span className="tnum">{countLabel(org.seatCount, 'seat')}</span>
          </span>
        }
        actions={
          isAdmin ? (
            <AppLink to="/org/$slug/members" params={{ slug: org.slug }}>
              <Button size="md">
                <UsersIcon size={14} />
                Members
              </Button>
            </AppLink>
          ) : null
        }
      />

      {notice ? (
        <div className="mb-6">
          <Banner
            tone={notice.tone}
            title={notice.title}
            action={
              isOwner && notice.ownerAction ? (
                <AppLink to="/org/$slug/billing" params={{ slug: org.slug }}>
                  <Button size="sm">{notice.ownerAction}</Button>
                </AppLink>
              ) : null
            }
          >
            {notice.body}
          </Banner>
        </div>
      ) : null}

      {isAdmin ? (
        <div className="mb-5">
          <div className="inline-flex items-center gap-1 rounded-md border border-hairline bg-slate-900 p-1">
            <ScopeTab active={effectiveScope === 'team'} onClick={() => setScope('team')}>
              Team workspace
            </ScopeTab>
            <ScopeTab active={effectiveScope === 'org'} onClick={() => setScope('org')}>
              Everything in this org
            </ScopeTab>
          </div>
          <p className="mt-2 text-caption text-chalk-faint">
            {effectiveScope === 'team'
              ? 'Recordings people deliberately shared with the team.'
              : 'Includes members’ personal recordings. Admins only. Be thoughtful about it.'}
          </p>
        </div>
      ) : null}

      {videos.isPending ? <VideoGridSkeleton /> : null}

      {videos.isError ? (
        <ErrorState
          title="Could not load this workspace"
          message={errorMessage(videos.error)}
          onRetry={() => void videos.refetch()}
        />
      ) : null}

      {videos.data && list.length === 0 ? (
        effectiveScope === 'org' ? (
          <EmptyState title="Nothing has been recorded in this org yet">
            <p>
              This view covers every recording made by every member, personal libraries included.
              It is empty because nobody has recorded anything yet.
            </p>
          </EmptyState>
        ) : (
          <EmptyState
            title="The team has not recorded anything yet"
            action={
              <AppLink to="/new">
                <Button variant="primary">
                  <RecordIcon size={11} />
                  Record something
                </Button>
              </AppLink>
            }
          >
            <p>
              A video shows up here when someone records with the workspace switcher set to{' '}
              <span className="text-chalk">{org.name}</span> instead of Personal. You can also move
              an existing recording over from its page.
            </p>
          </EmptyState>
        )
      ) : null}

      {list.length > 0 ? <VideoGrid videos={list} showOwner={effectiveScope === 'org'} /> : null}

      {videos.data?.nextCursor ? (
        <p className="mt-6 text-center text-caption text-chalk-faint">
          Showing the most recent recordings.
        </p>
      ) : null}
    </>
  );
}

export function OrgPage({ slug }: { slug: string }) {
  const { orgs, loading, error, refetch } = useWorkspace();

  if (loading) return <Loading label="Loading your workspaces" />;

  if (error) {
    return (
      <ErrorState
        title="Could not load your workspaces"
        message={errorMessage(error)}
        onRetry={refetch}
      />
    );
  }

  const org = orgs.find((candidate) => candidate.slug === slug);

  if (!org) {
    return (
      <>
        <PageHeader title="Workspace not available" />
        <EmptyState
          title="You are not a member of this workspace"
          icon={<UsersIcon size={26} />}
          action={
            <AppLink to="/">
              <Button variant="primary">Go to your library</Button>
            </AppLink>
          }
        >
          <p>
            Either the address is wrong, or someone removed your membership. If a teammate meant to
            add you, ask them to send a fresh invitation. You will get an email with a link.
          </p>
          <p className="mt-2">
            <Badge tone="neutral">{slug}</Badge>
          </p>
        </EmptyState>
      </>
    );
  }

  return <OrgWorkspace key={org.id} org={org} />;
}
